// src/api/exportFile.js
// 文件流导出统一封装：reqBlob 请求文件流，handleExportResult 分流（成功下载 / 失败弹窗）
// 配套后端 ExportFileResult + ToActionResult（成功 FileContentResult / 失败 OkObjectResult<ExportCheckResultDto>）
import axios from "axios";

const instance = axios.create({});

instance.interceptors.request.use(
	(config) => {
		const token = localStorage.getItem("logintoken");
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

// 请求文件流（responseType=blob），返回 axios 完整响应（含 headers）供 handleExportResult 分流
export function reqBlob(method, url, params) {
	return instance({
		method: method,
		url: url,
		data: params,
		headers: { "Content-Type": "application/json" },
		responseType: "blob"
	});
}

function decodeFileName(disposition) {
	if (!disposition) return "导出文件.xlsx";
	const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
	if (utf8) return decodeURIComponent(utf8[1]);
	const m = disposition.match(/filename="?([^";]+)"?/i);
	if (m) return m[1];
	return "导出文件.xlsx";
}

function triggerDownload(blob, fileName) {
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

// 统一处理导出响应：成功（xlsx blob）→ 浏览器下载；失败（JSON blob）→ 弹窗或提示
// res: axios 完整响应；that: 当前 vue 实例（用于弹窗/消息）
export function handleExportResult(res, that) {
	const headers = (res && res.headers) || {};
	const contentType = headers["content-type"] || "";
	const isExcel =
		contentType.indexOf("spreadsheetml") > -1 ||
		contentType.indexOf("excel") > -1 ||
		(contentType.indexOf("octet-stream") > -1 && res.data && res.data.size > 0);
	if (isExcel && res.data && res.data.size > 0) {
		const blob = new Blob([res.data], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
		});
		triggerDownload(blob, decodeFileName(headers["content-disposition"]));
		if (that && that.$message) that.$message.success("导出成功");
		return Promise.resolve(true);
	}
	// 失败分支：响应体为 JSON（被包装成 blob）
	const textPromise = res.data && res.data.text ? res.data.text() : Promise.resolve("{}");
	return textPromise.then((text) => {
		let json = {};
		try {
			json = JSON.parse(text);
		} catch (e) {
			json = {};
		}
		const data = json.data || {};
		if (data.blocks && data.blocks.length) {
			if (that) {
				that.exportBlockVisible = true;
				that.exportBlockTitle = data.title || "导出失败";
				that.exportBlockData = data.blocks;
			}
		} else if (that && that.$message) {
			that.$message.error((json && json.msg) || "导出失败");
		}
		return false;
	});
}

// 带按钮 loading 的统一导出：开启 loading → 请求文件流 → handleExportResult 分流 → 关闭 loading
// that: 当前 vue 实例；loadingKey: 按钮 :loading 绑定的 data 字段名（默认 exportLoading）
// request: () => Promise，导出接口调用（reqBlob 返回的 axios 响应），延迟到调用时才发起请求
// errorMsg: 请求异常时的提示文案，默认 "导出失败，请稍后再试！"
export function exportWithLoading(that, loadingKey, request, errorMsg) {
	const key = loadingKey || "exportLoading";
	if (that) {
		that[key] = true;
	}
	const closeLoading = () => {
		if (that) {
			that[key] = false;
		}
	};
	return Promise.resolve()
		.then(request)
		.then((res) => handleExportResult(res, that))
		.catch(() => {
			if (that && that.$message) {
				that.$message.error(errorMsg || "导出失败，请稍后再试！");
			}
		})
		.then((result) => {
			closeLoading();
			return result;
		});
}
